import { Address } from './address';
import { BaseItem, Nullable } from './common';
import { Encounter, EncounterWithPatient } from './encounter';
import { Patient, Race } from './patient';
import { Guarantor } from './patient/guarantor';
import { InsuranceInformation as PatientInsurance } from './patient/insuranceInformation';

type PhoneNumber = { phoneNumber: string };

type AddressData = {
  address: Pick<Address, 'address' | 'city' | 'state' | 'zip'>;
};

type PatientData = Nullable<
  Pick<
    Patient,
    'firstName' | 'lastName' | 'sex' | 'race' | 'dateOfBirth' | 'ssn4' | 'id'
  > &
    AddressData &
    PhoneNumber
>;

type GuarantorData = Nullable<
  Pick<
    Guarantor,
    | 'firstName'
    | 'lastName'
    | 'sex'
    | 'race'
    | 'dateOfBirth'
    | 'ssn4'
    | 'relationshipToPatient'
  > &
    AddressData &
    PhoneNumber
>;

type EncounterData = Nullable<
  Pick<
    Encounter,
    'buildingLocation' | 'medicalService' | 'ehrEncounterType'
  > & {
    admitDatetime: Date;
    admittingPhysicianId: string;
    attendingPhysicianId: string;
  }
>;

type Insurance = Nullable<
  {
    firstName: PatientInsurance['primaryPolicyHolderFirstName'];
    lastName: PatientInsurance['primaryPolicyHolderLastName'];
    sex: PatientInsurance['primaryPolicyHolderSex'];
    dateOfBirth: PatientInsurance['primaryPolicyHolderDob'];
    phoneNumber: PatientInsurance['primaryPolicyHolderHomePhone'];
    relationshipToPatient: PatientInsurance['patientRelationshipToPrimaryPolicyholder'];
    planName: PatientInsurance['planName'];
    policyNumber: PatientInsurance['memberId'];
    groupNumber: PatientInsurance['groupNumber'];
  } & { race: Race[] } & AddressData
>;

type Insurances = Nullable<{ primary: Insurance; secondary: Insurance }>;

type Clinical = Nullable<{
  visitReason: EncounterWithPatient['kindOfVisit'];
  gestationalAge: Patient['birthGestationalAge'];
  weightGrams: Patient['birthWeightGrams'];
}>;

type General = Nullable<{ mrn: string; fin: string }>;

export type PatientContext = Nullable<{
  clinical: Clinical;
  encounter: EncounterData;
  general: General;
  guarantor: GuarantorData;
  insurances: Insurances;
  patient: PatientData;
  template: string;
}>;

export type NoticeOfAdmission = Nullable<{
  newborn: PatientContext;
  mother: PatientContext;
}>;

export enum RejectedReason {
  Other = 'OTHER',
}

export enum Status {
  New = 'NEW',
  Pending = 'PENDING',
  Approved = 'APPROVED',
  Exception = 'EXCEPTION',
  Denied = 'DENIED',
}

export type UpsertStatus = { encounterId: string } & (
  | { status: Status.New }
  | { status: Status.Pending; authorizationNumber?: string | null }
  | { status: Status.Approved; authorizationNumber: string }
  | { status: Status.Exception }
  | {
      status: Status.Denied;
      rejectedReason: RejectedReason;
      rejectedComment?: string | null;
    }
);

interface BaseStatus {
  encounterId: string;
  current: boolean;
  status: Status;
  ehrStatus: string | null;
  rejectedReason: RejectedReason | null;
  rejectedComment: string | null;
  authorizationNumber: string | null;
}

export interface NoticeOfAdmissionStatus extends BaseItem, BaseStatus {}
